import { authenticatedGet, authenticatedPost, authenticatedPut, authenticatedDelete } from '../../api/authenticatedFetch';
const BASE_API = '/api/v1/financial-ownership';
export const getMatrix = async (params = {}) => authenticatedGet(`${BASE_API}/matrix`, params);
export const getCoverage = async (params = {}) => authenticatedGet(`${BASE_API}/coverage`, params);
// The preview handler wraps its result under a "preview" key ({"preview": {...}}).
// Unwrap it so callers get the impact object directly (affected_monthly_cost,
// confidence_label, …). Falls back to the raw response when unwrapped (e.g. test mocks).
export const previewRule = async (payload) => {
  const res = await authenticatedPost(`${BASE_API}/rules/preview`, payload);
  return res?.preview ?? res;
};
export const createRule = async (payload) => authenticatedPost(`${BASE_API}/rules`, payload);
// Replace a cell's whole owner split. payload: { sourceId, dimensionId, effectiveFrom,
// owners: [{ ownerNodeId, pct }] } where pct is a whole percentage (0..100). The remainder
// (100 - sum) flows to the catch-all (Unallocated).
export const setSplit = async (payload) => authenticatedPost(`${BASE_API}/rules/split`, payload);
// Bulk apply — POST one rule shape across N source ids in a single transaction. The
// backend is all-or-nothing, so the UI should surface any error verbatim instead of
// claiming partial success.
export const bulkCreateRules = async (payload) => authenticatedPost(`${BASE_API}/rules/bulk`, payload);
export const createSource = async (payload) => authenticatedPost(`${BASE_API}/sources`, payload);
export const getSourceValues = async ({ excludeSourceId = null, ...params } = {}) =>
  authenticatedGet(`${BASE_API}/sources/values`, {
    ...params,
    ...(excludeSourceId != null ? { exclude_source_id: Number(excludeSourceId) } : {}),
  });
// previewCleanupPlan returns the per-type sources a candidate whole-scope source
// would cover, with recommended archive/narrow actions. axis is "project" or
// "ai_workspace"; value is the single scope value. excludeSourceId excludes the
// source being edited so it never flags itself.
export const previewCleanupPlan = async (axis, value, excludeSourceId = null) =>
  authenticatedPost(`${BASE_API}/sources/cleanup-plan`, {
    axis,
    value,
    ...(excludeSourceId != null ? { exclude_source_id: Number(excludeSourceId) } : {}),
  });

// rankAheadSourceIds: when the builder's priority control is used, the ids of the sources
// placed ABOVE this one (they claim cost first), so the residual preview reflects the chosen
// slot. null = let the backend use its default priority heuristic; [] = top priority.
export const previewSource = async (criteria, sourceTypeId, excludeSourceId = null, rankAheadSourceIds = null) =>
  authenticatedPost(`${BASE_API}/sources/preview`, {
    ...criteria,
    source_type_id: sourceTypeId,
    // When editing, exclude this source so it doesn't report overlapping with itself.
    ...(excludeSourceId != null ? { exclude_source_id: Number(excludeSourceId) } : {}),
    ...(rankAheadSourceIds != null ? { rank_ahead_source_ids: rankAheadSourceIds.map(Number) } : {}),
  });

// Persist the full priority order of sources (first claims cost first). payload: number[] of
// source ids in the desired order, catch-all last. Used by the builder's visual order control.
export const reorderSources = async (sourceIds) =>
  authenticatedPost(`${BASE_API}/sources/reorder`, { sourceIds: sourceIds.map(Number) });

// Audit drill-down: returns the top ~10 (service, sku) groups by total cost over the last
// 30 days that match the criteria. Used by the SkuBreakdownModal so finance can verify the
// *composition* of a source ("does the spend mix look right?") instead of staring at a list
// of identical-looking rows.
export const getSkuBreakdown = async (criteria, sourceTypeId) =>
  authenticatedPost(`${BASE_API}/sources/sku-breakdown`, {
    ...criteria,
    source_type_id: sourceTypeId,
  });
export const getSource = async (id) => authenticatedGet(`${BASE_API}/sources/${id}`);
// Audit log: returns the change history for one source, newest first. Used by the
// SourceHistoryPanel in edit mode so finance can answer "who changed this source, when?".
export const getSourceHistory = async (id) => authenticatedGet(`${BASE_API}/sources/${id}/history`);
export const updateSource = async (id, payload) => authenticatedPut(`${BASE_API}/sources/${id}`, payload);
export const discoverSources = async () => authenticatedPost(`${BASE_API}/sources/discover`, {});
export const archiveSource = async (id) => authenticatedDelete(`${BASE_API}/sources/${id}`);
// Dismiss a detected (auto-discovered) source from the detected-sources screen only: the
// backend clears auto_discovered (and marks it acknowledged so discovery won't re-flag it)
// while keeping the source active and visible in every other allocation view.
export const dismissDiscoveredSource = async (id) => authenticatedPost(`${BASE_API}/sources/${id}/dismiss`, {});
export const validateCel = async (payload) => {
  // Backend wraps this endpoint's response as { validation_result: {...} }.
  const data = await authenticatedPost(`/api/v1/virtual-tags/validate`, payload);
  return data?.validation_result ?? data;
};
export const getEntityAliases = async ({ entityType, search = "", labelKey = "" } = {}) =>
  authenticatedGet(`${BASE_API}/aliases`, { entity_type: entityType, search, ...(labelKey ? { labelKey } : {}) });
export const putEntityAlias = async ({ entityType, entityKey, alias }) =>
  authenticatedPut(`${BASE_API}/aliases`, { entity_type: entityType, entity_key: entityKey, alias });
export const deleteEntityAlias = async ({ entityType, entityKey }) =>
  authenticatedPut(`${BASE_API}/aliases`, { entity_type: entityType, entity_key: entityKey, alias: "" });
